import cn from 'classnames'

import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Tag } from 'partner360/components/qualityIndicator'
import { CockpitCardHeader } from 'partner360/features/financialCockpit/ui/components/cockpitCardHeader'
import { CockpitIconButton } from 'partner360/features/financialCockpit/ui/components/cockpitIconButton'

import { PLAN_REVIEW_TRANSLATIONS } from '../config/translations'
import { formatPlanningMeasure } from '../lib/formatPlanningMeasure'
import type { IPlanningMeasureItem, IPlanningMeasureQuality } from '../model/planningMeasure'

import { JustificationBanner } from './justification/JustificationBanner'
import { PlanReviewText } from './PlanReviewText'

import styles from './PlanningMeasureStep.module.scss'

const copy = PLAN_REVIEW_TRANSLATIONS.planningMeasure

export const PlanningMeasureIssues = ({ issues }: { issues: readonly IPlanningMeasureItem[] }) => (
  <ul className={styles.issues}>
    {issues.map((item) => (
      <li key={`${item.offering}/${item.category}`}>
        <PlanReviewText
          text={copy.issue}
          replace={{
            offering: <Translation text={item.offering} span />,
            category: <Translation text={item.category} span />,
            actualsType: formatPlanningMeasure(item.actualsType),
            defaultActualsType: formatPlanningMeasure(item.defaultActualsType),
          }}
        />
      </li>
    ))}
  </ul>
)

const MeasureBadge = ({ quality }: { quality: IPlanningMeasureQuality }) => (
  <Tag
    variant={quality.band ?? undefined}
    translation={quality.score === null ? '-' : `${quality.score}%`}
  />
)

export const PlanningMeasureSummary = ({
  quality,
  onReview,
}: {
  quality: IPlanningMeasureQuality
  onReview: () => void
}) => {
  const t = useSafetyTranslation()

  return (
    <section className={cn(styles.root, styles.problem)}>
      <header className={styles.header}>
        <CockpitCardHeader
          title={<PlanReviewText text={copy.title} />}
          actions={
            <>
              <MeasureBadge quality={quality} />
              <CockpitIconButton
                showLabel
                icon="kbase kbase-chevron-right"
                label={t(PLAN_REVIEW_TRANSLATIONS.summary.reviewDetails.key, {
                  defaultValue: PLAN_REVIEW_TRANSLATIONS.summary.reviewDetails.fallback,
                })}
                onClick={onReview}
              />
            </>
          }
        />
      </header>
      <div className={styles.content}>
        <PlanningMeasureIssues issues={quality.issues} />
      </div>
    </section>
  )
}

export const PlanningMeasureStep = ({
  quality,
  fYear,
  justification,
  canEdit,
  onJustify,
}: {
  quality: IPlanningMeasureQuality
  fYear: number
  justification?: string
  canEdit: boolean
  onJustify: () => void
}) => {
  const actualsTypes = [...new Set(quality.items.map((item) => item.actualsType))]
    .map(formatPlanningMeasure)
    .join(', ')
  const defaultActualsType = formatPlanningMeasure(quality.defaultActualsType)

  return (
    <section className={cn(styles.root, { [styles.problem]: quality.band === 'Low' })}>
      <header className={styles.header}>
        <CockpitCardHeader
          title={
            <PlanReviewText
              text={actualsTypes ? copy.plannedUsing : copy.noSavedMeasure}
              replace={{ actualsType: actualsTypes }}
            />
          }
          description={<PlanReviewText text={copy.title} />}
          actions={<MeasureBadge quality={quality} />}
        />
      </header>
      <div className={styles.content}>
        <p>
          <PlanReviewText
            text={copy.requirement}
            replace={{
              fYear: String(fYear).slice(-2),
              actualsType: <strong className={styles.emphasis}>{defaultActualsType}</strong>,
              mandatoryRequirement: (
                <strong className={styles.emphasis}>
                  <PlanReviewText text={copy.mandatoryRequirement} />
                </strong>
              ),
            }}
          />
        </p>
        {quality.band === 'Low' && (
          <>
            <JustificationBanner
              justification={justification}
              canEdit={canEdit}
              onClick={onJustify}
              messageClassName={styles.justificationPrompt}
              message={
                <PlanReviewText
                  text={copy.justificationPrompt}
                  replace={{ actualsType: defaultActualsType }}
                />
              }
            />
            <PlanningMeasureIssues issues={quality.issues} />
          </>
        )}
      </div>
    </section>
  )
}
